// Privacy-compliant PostHog wrapper — EU-hosted, consent-gated.
//
// PostHog only initializes after the user accepts analytics cookies (same
// gate as GA4 in App.tsx), and the SDK is loaded via dynamic import so it
// never weighs down the initial bundle. Without VITE_POSTHOG_KEY every
// function is a no-op, so local dev and forks work without a PostHog project.
import type { PostHog } from "posthog-js";

let client: PostHog | null = null;
let loading = false;
let optedOut = false;

export function initializePostHog(canUseAnalytics: boolean) {
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!canUseAnalytics || !key || typeof window === "undefined") return;

  optedOut = false;

  if (client) {
    // Consent re-granted after an opt-out — resume capture.
    client.opt_in_capturing();
    return;
  }
  if (loading) return;
  loading = true;

  import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.init(key, {
        api_host:
          (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
          "https://eu.i.posthog.com",
        // Only create person profiles when identify() is called — anonymous
        // visitors stay anonymous (and cost nothing extra).
        person_profiles: "identified_only",
        // SPA-aware pageviews on route changes.
        capture_pageview: "history_change",
        // Events are explicit via trackEvent — no DOM autocapture noise.
        autocapture: false,
        capture_performance: false,
        // Uncaught errors and unhandled rejections as $exception events.
        // Until 2026-09 nothing in the client reported failures anywhere we
        // could read — a user who hit a crash was invisible unless they
        // filed feedback. Same consent gate as everything else here.
        capture_exceptions: true,
      });
      client = posthog;
      // Consent may have been revoked while the SDK was downloading.
      if (optedOut) client.opt_out_capturing();
    })
    .catch(() => {
      loading = false; // allow a retry on the next consent change
    });
}

export function disablePostHog() {
  optedOut = true;
  client?.opt_out_capturing();
}

/** Fire-and-forget capture; safe to call before init (no-op). */
export function posthogCapture(
  eventName: string,
  params?: Record<string, string | number | boolean>,
) {
  if (!client || client.has_opted_out_capturing()) return;
  client.capture(eventName, params);
}
